import {
  checkInvariants,
  cloneState,
  createMatch,
  makeConfig,
  parseFace,
  parseSquare,
  sameSquare,
  type ConfigOptions,
  type Epitaph,
  type Face,
  type MatchConfig,
  type MatchState,
  type Pawn,
  type Phase,
  type Side,
  type Square,
} from '../engine';

/**
 * Builds a match position from a short description: where the pawns stand, which cards are in
 * each hand and deck, the top of the pile, who acts and in which phase. Positions need not be
 * reachable from the start of a match (spec §23); the tutorial and the tests use them.
 */
export interface PositionSpec {
  preset?: 'A' | 'B' | 'BASELINE';
  mode?: 'duel' | 'campaign';
  config?: ConfigOptions;
  /** A complete match config (e.g. a campaign table); overrides preset, mode and config. */
  matchConfig?: MatchConfig;
  /** e.g. ['C1', 'C2 (crowned)']; omit to keep the starting position. */
  south?: string[];
  north?: string[];
  /** Face codes, e.g. ['R1', 'W']. */
  hands?: Partial<Record<Side, string[]>>;
  /** Exact deck contents, top first. Unassigned cards go to Spent. Omit to keep the shuffled deck. */
  decks?: Partial<Record<Side, string[]>>;
  /** Top of the pile, e.g. 'B2' or 'W=R2'; null or omitted for an empty pile. */
  top?: string | null;
  toAct?: Side;
  phase?: Phase;
  round?: number;
  epitaphs?: Partial<Record<Side, Epitaph[]>>;
  /** Sides that have already passed this round (never the side to act). */
  passed?: Side[];
  /** Round scores so far. */
  scores?: Partial<Record<Side, number>>;
  seed?: string | number;
}

const parsePawnSpec = (text: string): { square: Square; crowned: boolean } => {
  const m = /^([A-F][1-6])\s*(\(crowned\))?$/.exec(text.trim());
  if (!m) throw new Error(`bad pawn spec ${text}`);
  return { square: parseSquare(m[1] as string) as Square, crowned: !!m[2] };
};

/** Builds a position; throws when the description is impossible or breaks an invariant. */
export function buildPosition(spec: PositionSpec = {}): MatchState {
  const config =
    spec.matchConfig ??
    makeConfig({
      preset: spec.preset ?? 'A',
      mode: spec.mode ?? 'duel',
      seating: { players: ['south', 'north'], assignment: 'fixed' },
      ...spec.config,
    });
  const s = cloneState(createMatch(config, spec.seed ?? 'test-position'));
  const toAct = spec.toAct ?? 'S';

  for (const side of ['S', 'N'] as const) {
    const list = side === 'S' ? spec.south : spec.north;
    if (!list) continue;
    const pawns = Object.values(s.pawns).filter((p) => p.side === side);
    const used = new Set<string>();
    const placements = list.map(parsePawnSpec);
    const assigned: { pawn: Pawn; square: Square; crowned: boolean }[] = [];
    // Prefer the pawn whose starting file matches, then any unused pawn.
    for (const pl of placements) {
      const byFile = pawns.find((p) => !used.has(p.id) && p.startSquare.x === pl.square.x);
      if (byFile) {
        used.add(byFile.id);
        assigned.push({ pawn: byFile, ...pl });
      }
    }
    for (const pl of placements) {
      if (assigned.some((a) => sameSquare(a.square, pl.square))) continue;
      const any = pawns.find((p) => !used.has(p.id));
      if (!any) throw new Error('too many pawns');
      used.add(any.id);
      assigned.push({ pawn: any, ...pl });
    }
    for (const p of pawns)
      s.pawns[p.id] = { ...p, status: 'captured', square: null, crowned: false, inscription: null };
    for (const a of assigned)
      s.pawns[a.pawn.id] = {
        ...a.pawn,
        status: 'onBoard',
        square: a.square,
        crowned: a.crowned,
        inscription: null,
      };
  }

  // Cards: gather each side's cards, then deal hands / decks / pile as requested.
  const pool: Record<Side, string[]> = { S: [], N: [] };
  for (const side of ['S', 'N'] as const) {
    const pl = s.players[side];
    pool[side] = [...pl.hand, ...pl.deck, ...pl.spent, ...pl.removed];
  }
  for (const e of s.pile) {
    const card = e.source === 'card' ? s.cards[e.cardId] : undefined;
    if (e.source === 'card' && card) pool[card.owner].push(e.cardId);
  }
  s.pile = [];
  const take = (side: Side, code: string): string => {
    const idx = pool[side].findIndex((id) => {
      const c = s.cards[id];
      if (!c) return false;
      return code === 'W'
        ? c.kind === 'wild'
        : c.kind === 'normal' && !!c.face && `${c.face.color}${c.face.number}` === code;
    });
    if (idx < 0) throw new Error(`no ${code} card left for ${side}`);
    return pool[side].splice(idx, 1)[0] as string;
  };

  if (spec.top) {
    const other: Side = toAct === 'S' ? 'N' : 'S';
    if (spec.top.startsWith('W=')) {
      const face = parseFace(spec.top.slice(2)) as Face;
      const id = take(other, 'W');
      s.pile.push({ source: 'card', cardId: id, face, wasWild: true, playedBy: other });
    } else {
      const face = parseFace(spec.top) as Face;
      const id = take(other, spec.top);
      s.pile.push({ source: 'card', cardId: id, face, wasWild: false, playedBy: other });
    }
  }
  for (const side of ['S', 'N'] as const) {
    const pl = s.players[side];
    const handCodes = spec.hands?.[side];
    const deckCodes = spec.decks?.[side];
    const hand = handCodes ? handCodes.map((c) => take(side, c)) : null;
    const deck = deckCodes ? deckCodes.map((c) => take(side, c)) : null;
    const original = [...s.players[side].hand, ...s.players[side].deck];
    const rest = pool[side];
    if (hand) pl.hand = hand;
    else {
      pl.hand = original.filter((id) => rest.includes(id)).slice(0, 7);
      for (const id of pl.hand) rest.splice(rest.indexOf(id), 1);
    }
    if (deck) {
      pl.deck = deck;
      pl.spent = [...rest];
    } else {
      pl.deck = original.filter((id) => rest.includes(id));
      pl.spent = rest.filter((id) => !pl.deck.includes(id));
    }
    pl.removed = [];
    pl.epitaphs = spec.epitaphs?.[side] ?? [];
    pl.roundScore = spec.scores?.[side] ?? 0;
    if (spec.passed?.includes(side)) {
      if (side === toAct) throw new Error('the side to act cannot have passed');
      pl.passed = true;
      pl.passKind = 'voluntary';
    }
  }
  s.round = spec.round ?? 1;
  s.turn = 1;
  s.active = toAct;
  s.decider = toAct;
  s.roundStarter = toAct;
  s.phase = spec.phase ?? 'ACTION';
  s.chain = null;
  // Keep engine-generated Epitaph ids clear of fixture ids.
  s.nextEpitaphSeq = 100;
  const problems = checkInvariants(s);
  if (problems.length > 0)
    throw new Error(`the position violates invariants: ${problems.join('; ')}`);
  return s;
}
